/**
 * The guide's step, target and runtime-seam types (Captain Bram, guide.ts re-exports all of it): what a script
 * declares and what a screen hands the controller. Types and constants only, with no DOM access at module load.
 */
import type { EndFacts, HubFacts, LevelFacts } from "./guideFacts";

// ------------------------------------------------------------------------------------------------ the character

/**
 * The attitude a step asks of him: the three original busts, plus the Bram v2 gestures (art/hq/guide README):
 * `thumbsUp` for a job well done, `laughing` for a light-hearted line. The live model plays their clips; the static
 * fallbacks are guideWidgets.ts POSE_ART.
 */
export type GuidePose = "neutral" | "pointing" | "cheering" | "thumbsUp" | "laughing";

// ------------------------------------------------------------------------------------------------ steps

/**
 * How a step presents itself and what ends it:
 * - `welcome`: a full-screen card with the big bust and one CTA; ends on the CTA.
 * - `do`: teach by doing. A spotlight and a pointer on the target; ends only when `until` holds and **never times out**.
 * - `info`: a line to read. It ends on a tap anywhere (accepted after GuideTiming.TapGuardMs) or when `until` holds.
 *   It never auto-dismisses.
 * - `tip`: non-blocking, with no dim. It ends after tipHoldMs(text) of visible, un-stalled time, or on its ✕, or when
 *   `until` holds.
 */
export type GuideKind = "welcome" | "do" | "info" | "tip";

/**
 * What the dim layer does with a tap outside the spotlight:
 * - `hard`: only the hole (and the guide's own buttons) takes it; elsewhere the bubble nudges. It never skips.
 * - `soft`: the dim is drawn but every tap passes through (the map stays playable).
 * - `none`: no dim.
 */
export type GuideBlock = "hard" | "soft" | "none";

/** A place in the level's 3D world that the host resolves to screen space every frame (GuideWorld.locate). */
export type WorldAnchor =
  | "base"                  // the fortress
  | "portal"                // where raiders come out (the road's far end)
  | "ghost"                 // the picked road card's shown option (its far end)
  | "towerSpot"             // the tower spot the player chose
  | "recommendedTowerCell"  // the legal cell that covers the most road for the tower card in hand
  | "firstTower"            // the first non-base tower built
  | "boss"                  // the living boss
  | "firstFlyer"            // the nearest living air enemy
  | "newEnemy"              // the nearest living enemy of the archetype a `enemy:<id>` signal named
  | "leadEnemy"             // the living enemy nearest the fortress (the one a tap would most likely inspect)
  | "elite";                // the living elite (an affix) nearest the fortress

export type GuideTarget =
  /**
   * A DOM target: the first visible match of `dom`, resolved on every layout. `hole` is the spotlight when it should
   * be larger than the pointer's target (the whole card row, with the pointer on one card).
   */
  | { readonly dom: string; readonly hole?: string; readonly pad?: number }
  /** A world target: `radiusCells` is the size of the hole; `focus` eases the camera to it when the step starts. */
  | { readonly world: WorldAnchor; readonly radiusCells?: number; readonly focus?: boolean };

export type GuideScreen = "title" | "hub" | "level" | "end";

/** The replayable topics of the Guide sheet (More → Guide). Every step names the one it teaches. */
export type GuideTopicId =
  | "roads" | "towers" | "points" | "upgrades" | "speed" | "soldiers" | "stars" | "map"
  | "flyers" | "shieldbearers" | "healers" | "bombers" | "bosses" | "enemies"
  | "gear" | "chests" | "builders" | "orders"
  /** M30 #9: Gold, gems and Marks — what each is, how it is earned and what it buys. */
  | "currencies"
  /** M22 gear v2: Forge Essence, raising, fusion and the free routes (features/forge.ts). */
  | "forge"
  /** M35: Gold Research, the per-tower perk trees (features/research.ts). */
  | "research"
  /** MP-22: the season pass, "War Campaign" (features/season.ts). */
  | "pass"
  /** WP9: the enemy card, "weak to" and elites; comfort settings (left-handed, larger text). */
  | "scouting" | "settings"
  /** TOWERS_V2: the four towers that work differently (topics.ts, group "towers"). */
  | "mortar" | "barracks" | "arcane" | "hex"
  /** M21 enemies (docs/ENEMIES_V2.md): one topic each, opened by the first sighting. */
  | "summoners" | "burrowers" | "spellwards" | "stunners"
  /** M21: tier IV — the Ascend card, the branches and their skill cards (docs/TOWERS_V2.md "Tier 4"). */
  | "ascend"
  | FeatureTopicId;

/**
 * The topics later features add through the feature registry (features/registry.ts `guideTopic`), agreed up front
 * (FINISH_PLAN WP0.6, WP9 O6) so scripts can name them before the feature lands.
 */
export type FeatureTopicId = "shop" | "market" | "events" | "queue" | "checkpoint" | "undo" | "achievements" | "skins" | "tribute"
  /** M21 (docs/MODES.md): Iron challenges and Endless mode. */
  | "iron" | "endless"
  /** MP-20 (docs/MODES.md §4): Daily Road. */
  | "daily"
  /** MP-21 (docs/MODES.md §5): the weekly event modes. */
  | "bossWeek" | "raceWeek" | "odyssey"
  /** MP-06/MP-25: pre-battle supplies and farm twists (features/supplies.ts). */
  | "supplies"
  /** MP-27, MP-28 (docs/MODES.md §7–§8): the Hall of Records and challenge codes. */
  | "records" | "challenge"
  /** MP-23 (docs/SHOP.md §9.7): Trophies and the Trophy Hall. */
  | "trophies"
  /** docs/PETS.md: the pets (features/pets.ts). */
  | "pets";

/**
 * One-shot events the screens report (GuideApi.signal). The controller sees each in the next update() only.
 * `enemy:<id>` is the first spawn of an archetype this run; `tower:<id>` is a newly unlocked tower card in an offer.
 */
export type GuideSignal =
  | "cardPicked" | "cardUnpicked" | "roadPlaced" | "towerSpotChosen" | "towerPlaced" | "upgradeUsed" | "offerClosed"
  | "wavesStarted" | "nextWaveCalled" | "speedChanged" | "pauseToggled" | "rallyArmed" | "rallyPlanted"
  | "towerSheetOpened" | "mapMoved" | "bossSpawned" | "stageStarted" | "lastStand"
  | "chestOpened" | "gearEquipped" | "upgradeStarted" | "playPressed" | "tabOpened" | "gearRaised" | "gearFused"
  | "wildPetAppeared"
  | `enemy:${string}` | `tower:${string}`;

export interface GuideContext {
  readonly screen: GuideScreen;
  readonly level: LevelFacts | null;
  readonly hub: HubFacts | null;
  readonly end: EndFacts | null;
  readonly highestCleared: number;
  /** Signals since the previous update(). */
  readonly signals: ReadonlySet<GuideSignal>;
}

/**
 * A step's words. They are plain text: `**x**` marks emphasis (drawn as <b>) and nothing else is interpreted (the
 * overlay builds text nodes, never innerHTML). A function is re-read on every update, so the line can follow the
 * player ("Tap ROTATE…" once a road card with two fits is picked). Keep it to at most 2 sentences and 22 words.
 */
export type GuideText = string | ((ctx: GuideContext) => string);

export interface GuideStep {
  /** Stable and saved: "l1.pickRoad", "hint.flyers", "hub.equip". Never reuse an id for new meaning. */
  readonly id: string;
  readonly kind: GuideKind;
  readonly pose: GuidePose;
  readonly text: GuideText;
  /** Welcome card ribbon ("WELCOME, COMMANDER!"). */
  readonly title?: string;
  /** Button label on welcome cards (default "OK"); info steps use "Tap to continue". */
  readonly cta?: string;
  /** The welcome card's secondary link ("I've played before"): it skips the walkthrough, not the tips. */
  readonly altCta?: string;
  readonly target?: GuideTarget;
  /** A second ring that takes no taps: the glowing road while the pointer is on PLACE. */
  readonly ring?: WorldAnchor;
  /** Default by kind: welcome hard, do hard, info hard (a tap anywhere continues), tip none. */
  readonly block?: GuideBlock;
  /** Draw the tapping glove on the target. Defaults to true for `do` steps with a target. */
  readonly pointer?: boolean;
  /** Where the bubble goes; `auto` puts it on the roomier side, away from the hole. */
  readonly placement?: "auto" | "top" | "bottom";
  /** The screen it belongs to; it is never shown on another. */
  readonly screen: GuideScreen;
  /** It may start now (checked on each update while it is queued). */
  readonly when: (ctx: GuideContext) => boolean;
  /** It has done its job (a `do` step's goal; for other kinds it ends the step early). Marks it done. */
  readonly until?: (ctx: GuideContext) => boolean;
  /** Pause the level sim while this step is on screen (the level's "guide" pause cause). */
  readonly holdSim?: boolean;
  /** Higher goes first. Default GuidePriority.Tip. */
  readonly priority?: number;
  /** Only once these ids are done (script order). */
  readonly after?: readonly string[];
  /**
   * Never once any of these ids is done: another line has already said it (the level's "New tower: Fire!" makes the
   * hub's "The Fire tower is ready!" old news). The step is simply not wanted; it is not marked done.
   */
  readonly unless?: readonly string[];
  /** Dropped from the queue, not marked done, if still unshown this long after `when` first held. Default: none. */
  readonly maxWaitMs?: number;
  /** Part of the level-1 walkthrough: skipped with it, and replayed by Practice. */
  readonly walkthrough?: boolean;
  readonly topic?: GuideTopicId;
  /** Walkthrough progress pips: this step's position and the count, e.g. [2, 9]. */
  readonly pip?: readonly [number, number];
  /**
   * Start only while the target is on screen (a DOM match with a size, or a world anchor that resolves). Until then
   * the step is not queued at all, so a hint about a button a feature has not drawn (or not landed) never holds up
   * the check-in card or the toasts, and never points at nothing.
   */
  readonly needsTarget?: boolean;
  /**
   * At most one step with this flag shows per visit to its screen (a return to the hub, a new level): a returning
   * player meets the features added since they last played one at a time, not as a string of tips.
   */
  readonly onePerVisit?: boolean;
}

export type GuideScriptId = "hub" | "level1" | "levelHints" | "end";
export interface GuideScript { readonly id: GuideScriptId; readonly steps: readonly GuideStep[] }

export const GuidePriority = { Welcome: 120, Walkthrough: 100, Safety: 80, NewThing: 60, Tip: 40 } as const;

// ------------------------------------------------------------------------------------------------ runtime seams

/** A world anchor on screen this frame, in CSS px: its centre and a radius (about a cell). */
export interface ScreenAnchor { readonly x: number; readonly y: number; readonly r: number }

/** The level's projection of world anchors to the screen (WP-B, level/guideLevel.ts, from rig.cellOnScreen). */
export interface GuideWorld {
  /** null when the anchor does not exist now (no boss alive, no card in hand) or is behind the camera. */
  locate(anchor: WorldAnchor): ScreenAnchor | null;
  /** Ease the camera so the anchor is in view (rig.glideToCell); a no-op if it already is. */
  focus(anchor: WorldAnchor): void;
}

/** Why the guide must wait before starting a step (queued steps wait; a showing tip steps aside and resumes). */
export type BusyReason =
  | "intro" | "banner" | "callout" | "tooltip" | "sheet" | "dialog" | "chest" | "unlockCards" | "loading" | "paused" | "wipe";

export interface GuideHost {
  /** Where the overlay mounts (the screen's ui root). */
  readonly root: HTMLElement;
  /** Level only. */
  readonly world: GuideWorld | null;
  /** Something else owns the player's attention now; null when the guide may speak. */
  busy(): BusyReason | null;
  /** Level only: a holdSim step started (true) or ended (false). The level adds or lifts its "guide" pause cause. */
  hold?(on: boolean): void;
  /**
   * M45: the element that is this screen's own surface (the hub's lobby, the level's 3D view; default `root`). The
   * guide shows a line only while what takes a tap at the middle of the screen belongs to it (or to the step's target's
   * layer): a sheet, dialog, banner or reveal over it puts the line aside (guideDom.ts surfaceClear).
   */
  screen?(): Element | null;
}

export interface GuideApi {
  register(script: GuideScript): void;
  /** A screen mounted (hub render, level built, end screen shown). Replaces the previous host for that screen. */
  attach(screen: GuideScreen, host: GuideHost): void;
  /** The screen went away: its step is withdrawn (not marked done) and any sim hold is released. */
  detach(screen: GuideScreen): void;
  /** New facts. Cheap: call it after every sim change or hub render. The controller diffs. */
  update(ctx: GuideContext): void;
  signal(s: GuideSignal): void;
  /** The step on screen, or null. */
  readonly active: string | null;
  /** The step on screen holds the sim. */
  readonly holdsSim: boolean;
  /**
   * Other pop-ups (the check-in card, the install card, news and order toasts) may show now. False while a step is on
   * screen or queued, and while the first-session scripts (the welcome to hub.play2) are unfinished.
   */
  quiet(): boolean;
  isDone(id: string): boolean;
  /** Skip the level-1 walkthrough (and the hub's first-session steps); contextual tips stay on. */
  skipWalkthrough(): void;
  /**
   * The walkthrough was skipped (Skip tips, or "I've played before"): the level ends its ×1 override on it (DESIGN §7).
   * Optional, an addition after commit 0: absent reads as false.
   */
  readonly walkthroughSkipped?: boolean;
  readonly tipsOff: boolean;
  /** The "Captain's tips" toggle (Settings, Guide sheet): off silences every `tip` step and every non-walkthrough level step but the warnings (priority Safety: boss, flyers). The walkthrough and hub steps stay. */
  setTipsOff(off: boolean): void;
  /** The Guide sheet plays a topic's lines (info cards, no targets) inside `host`, then calls `done`. */
  replayTopic(topic: GuideTopicId, host: HTMLElement, done: () => void): void;
  /** "Practice battle with me": clears the walkthrough's done ids so the next level-1 run teaches again. */
  startPractice(): void;
}
